import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Snackbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import { usePageContext } from "../../context/PageContext";
import { useAuthContext } from "../../context/AuthContext";
import { fetchData } from "../../api/client";
import { useEffectivePermissions, toSet, can } from "../../hooks/useEffectivePermissions";
import EmptyStateWithAI from "../../components/EmptyStateWithAI";
import ChatbotFlowWorkspace from "../../components/ChatbotFlow/ChatbotFlowWorkspace";
import { useTranslation } from "react-i18next";

export default function ChatbotForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("settings");
  const { userData } = useAuthContext();
  const { setCurrentPage } = usePageContext();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isNew = !id;

  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(permData?.scopes), [permData?.scopes]);
  const canUpdate = can(scopeSet, "chatbot", "update");
  const canCreate = can(scopeSet, "chatbot", "create");

  const userId = id ? parseInt(id, 10) : null;
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [botName, setBotName] = useState("");

  const [creatingBot, setCreatingBot] = useState(false);
  const [newBotName, setNewBotName] = useState("");
  const [openCreateBot, setOpenCreateBot] = useState(false);
  const [editingBot, setEditingBot] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [creatingWithAI, setCreatingWithAI] = useState(false);

  const loadBotMeta = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetchData(`/users/${userId}`, "GET");
      const u = res?.data ?? res;
      setBotName(u?.nome ?? "");
    } catch {
      setBotName("");
    }
  }, [userId]);

  useEffect(() => {
    if (!isNew && userId) {
      loadBotMeta();
    }
  }, [isNew, userId, loadBotMeta]);

  useEffect(() => {
    setNameDraft(botName || "");
  }, [botName]);

  useEffect(() => {
    const title = isNew ? t("chatbotFormPage.newTitle") : botName || t("chatbotConfigPage.title");
    setCurrentPage({ title, showBackButton: false });
  }, [setCurrentPage, isNew, botName, t]);

  const createDefaultStructure = useCallback(async (targetUserId) => {
    await fetchData("/chatbot/slots/generate-initial", "POST", {
      user_id: targetUserId,
    });
  }, []);

  const handleCreateBotUser = async () => {
    if (!canCreate) {
      setSnack({ open: true, severity: "warning", message: t("chatbotFormPage.noPermissionCreate") });
      return;
    }
    if (!newBotName.trim()) {
      setError(t("chatbotConfigPage.nameRequired"));
      return;
    }
    setCreatingBot(true);
    try {
      setError(null);
      const created = await fetchData("/users", "POST", {
        nome: newBotName.trim(),
        status: 1,
        ura: 1,
        incluir_nome: 0,
        id_usuario: userData?.id ?? null,
        id_usuario_ultima_alteracao: userData?.id ?? null,
      });
      const createdUserId = created?.data?.id ?? created?.id;
      setOpenCreateBot(false);
      setNewBotName("");
      queryClient.invalidateQueries(["chatbots"]);
      if (createdUserId) {
        navigate(`/chatbots/${createdUserId}`, { replace: true });
      } else {
        navigate("/chatbots", { replace: true });
      }
    } catch (err) {
      const baseMsg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        t("chatbotConfigPage.errorCreateUser");
      const detail =
        (typeof err?.data === "string" && err.data) ||
        (typeof err?.details === "string" && err.details) ||
        null;
      setError(detail ? `${baseMsg} (${detail})` : baseMsg);
    } finally {
      setCreatingBot(false);
    }
  };

  const handleCreateBotUserWithAI = async () => {
    if (!canCreate) {
      setSnack({ open: true, severity: "warning", message: t("chatbotFormPage.noPermissionCreate") });
      return;
    }
    setCreatingWithAI(true);
    try {
      setError(null);
      const name = `ChatBot ${new Date().toLocaleDateString("pt-BR")}`;
      const created = await fetchData("/users", "POST", {
        nome: name,
        status: 1,
        ura: 1,
        incluir_nome: 0,
        id_usuario: userData?.id ?? null,
        id_usuario_ultima_alteracao: userData?.id ?? null,
      });
      const createdUserId = created?.data?.id ?? created?.id;
      if (createdUserId) {
        await createDefaultStructure(createdUserId);
        queryClient.invalidateQueries(["chatbot", "slots", createdUserId]);
        queryClient.invalidateQueries(["chatbot"]);
        queryClient.invalidateQueries(["chatbots"]);
        navigate(`/chatbots/${createdUserId}`, { replace: true });
      } else {
        queryClient.invalidateQueries(["chatbots"]);
        navigate("/chatbots", { replace: true });
      }
    } catch (err) {
      const baseMsg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        t("chatbotConfigPage.errorCreateAI");
      const detail =
        (typeof err?.data === "string" && err.data) ||
        (typeof err?.details === "string" && err.details) ||
        null;
      setError(detail ? `${baseMsg} (${detail})` : baseMsg);
    } finally {
      setCreatingWithAI(false);
    }
  };

  const nameDirty = useMemo(
    () => nameDraft.trim() !== (botName || "").trim(),
    [nameDraft, botName]
  );

  const handleSaveBotName = async () => {
    if (!userId) return;
    if (!nameDraft.trim()) {
      setError(t("chatbotConfigPage.nameRequired"));
      return;
    }
    setEditingBot(true);
    try {
      setError(null);
      await fetchData(`/users/${userId}`, "PUT", {
        nome: nameDraft.trim(),
        id_usuario_ultima_alteracao: userData?.id ?? null,
      });
      setBotName(nameDraft.trim());
      queryClient.invalidateQueries(["chatbots"]);
      setSnack({ open: true, severity: "success", message: t("chatbotFormPage.snackRenamed") });
    } catch (err) {
      const baseMsg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        t("chatbotConfigPage.errorEditUser");
      const detail =
        (typeof err?.data === "string" && err.data) ||
        (typeof err?.details === "string" && err.details) ||
        null;
      setError(detail ? `${baseMsg} (${detail})` : baseMsg);
    } finally {
      setEditingBot(false);
    }
  };

  if (isNew) {
    return (
      <Box sx={{ pt: 1, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/chatbots")}
          >
            {t("chatbotFormPage.backToList")}
          </Button>
        </Stack>
        <Typography variant="h4" component="h1" gutterBottom>
          {t("chatbotFormPage.newTitle")}
        </Typography>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <EmptyStateWithAI
          title={t("chatbotConfigPage.emptyTitle")}
          description={t("chatbotConfigPage.emptyDescription")}
          primaryLabel={t("chatbotConfigPage.createWithAI")}
          secondaryLabel={t("chatbotConfigPage.createManually")}
          onPrimaryClick={handleCreateBotUserWithAI}
          onSecondaryClick={() => setOpenCreateBot(true)}
          primaryLoading={creatingWithAI}
          icon={<SmartToyOutlined sx={{ fontSize: 56 }} color="primary" />}
          maxWidth="920px"
        />

        <Dialog open={openCreateBot} onClose={() => !creatingBot && setOpenCreateBot(false)} fullWidth maxWidth="xs">
          <DialogTitle>{t("chatbotConfigPage.newChatbot")}</DialogTitle>
          <DialogContent dividers>
            <TextField
              autoFocus
              margin="dense"
              label={t("chatbotConfigPage.chatbotName")}
              fullWidth
              value={newBotName}
              onChange={(e) => setNewBotName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreateBot(false)} disabled={creatingBot}>
              {t("shared.cancel")}
            </Button>
            <Button onClick={handleCreateBotUser} disabled={creatingBot} variant="contained">
              {t("chatbotConfigPage.create")}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          message={snack.message}
        />
      </Box>
    );
  }

  if (!userId || Number.isNaN(userId)) {
    return (
      <Box sx={{ pt: 2 }}>
        <Alert severity="warning">{t("chatbotFormPage.invalidId")}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/chatbots")}>
          {t("chatbotFormPage.backToList")}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate("/chatbots")}
        >
          {t("chatbotFormPage.backToList")}
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<BarChartRoundedIcon />}
          onClick={() => navigate(`/chatbots/${userId}/analytics`)}
        >
          {t("agentsListPage.analytics")}
        </Button>
      </Box>

      {canUpdate ? (
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={1.5}
          alignItems={isMobile ? "stretch" : "flex-start"}
          sx={{ mb: 2 }}
        >
          <TextField
            label={t("chatbotConfigPage.chatbotName")}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && nameDirty && !editingBot) {
                e.preventDefault();
                handleSaveBotName();
              }
            }}
            size="small"
            fullWidth={isMobile}
            sx={{ minWidth: { sm: 280 }, maxWidth: { sm: 480 } }}
            disabled={editingBot}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!nameDirty || editingBot}
            onClick={handleSaveBotName}
            sx={{ alignSelf: isMobile ? "stretch" : "auto", mt: { sm: "4px !important" } }}
          >
            {t("shared.save")}
          </Button>
        </Stack>
      ) : (
        <Typography variant="h4" component="h1" gutterBottom>
          {botName || t("chatbotConfigPage.heading")}
        </Typography>
      )}

      <ChatbotFlowWorkspace userId={userId} error={error} setError={setError} />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
}
